
import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import ContentWithAds from '../../components/ContentWithAds';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const FranchiseDashboard = ({ user }) => {
    const navigate = useNavigate();
    const [franchiseData, setFranchiseData] = useState(null);
    const [franchiseId, setFranchiseId] = useState(null);
    const [totalUsers, setTotalUsers] = useState(0);
    const [totalCompanies, setTotalCompanies] = useState(0);
    const [totalJobs, setTotalJobs] = useState(0);
    const [totalSubscriptions, setTotalSubscriptions] = useState(0);
    const [remainingQuota, setRemainingQuota] = useState(0);
    const [remainingDays, setRemainingDays] = useState(0);
    const [recentJobs, setRecentJobs] = useState([]);
    const [topCompanies, setTopCompanies] = useState([]);
    const [salesData, setSalesData] = useState({ labels: [], datasets: [] });
    const [referralLink, setReferralLink] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);
    const [loading, setLoading] = useState(true);

    // Update referral link when franchiseId or linkType changes
    // Update referral link when franchiseId changes
    useEffect(() => {
        if (franchiseId) {
            // Use window.location.origin to get correct domain in dev/prod
            const domain = window.location.origin === 'http://localhost:3000'
                ? 'http://localhost:3000'
                : 'https://harshaljobs.com';

            // Generate simple link without user type so user can select role on register page
            const link = `${domain}/register?ref=${franchiseId}`;
            setReferralLink(link);
        }
    }, [franchiseId]);

    useEffect(() => {
        const fetchFranchiseData = async () => {
            try {
                if (user?.uid) {
                    // Fetch franchiseId from users collection
                    const userDoc = await getDoc(doc(db, 'users', user.uid));
                    if (!userDoc.exists()) {
                        console.error("User document not found!");
                        setLoading(false);
                        return;
                    }

                    // Get franchiseId (should be same as uid, but check user document first)
                    const userData = userDoc.data();
                    const fId = userData.franchiseId || user.uid;
                    setFranchiseId(fId);

                    // Fetch franchise document
                    const franchiseDoc = await getDoc(doc(db, 'franchises', fId));

                    if (!franchiseDoc.exists()) {
                        console.error("No franchise document found!");
                        setLoading(false);
                        return;
                    }

                    const fData = franchiseDoc.data();
                    setFranchiseData(fData);



                    // Calculate remaining quota
                    const remaining = (fData.totalQuota || 0) - (fData.usedQuota || 0);
                    setRemainingQuota(remaining);

                    // Calculate remaining validity days
                    if (fData.validityEnd) {
                        const endDate = new Date(fData.validityEnd);
                        const today = new Date();
                        const diffTime = endDate - today;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        setRemainingDays(diffDays > 0 ? diffDays : 0);
                    }

                    // Fetch franchise users (count)
                    // Query main users collection by referredBy to handle all users
                    const usersQuery = query(
                        collection(db, 'users'),
                        where('referredBy', '==', fId)
                    );
                    const usersSnapshot = await getDocs(usersQuery);
                    setTotalUsers(usersSnapshot.size);

                    // Fetch companies belonging to this franchise
                    const companiesQuery = query(
                        collection(db, 'companies'),
                        where('franchiseId', '==', fId)
                    );
                    const companiesSnapshot = await getDocs(companiesQuery);
                    setTotalCompanies(companiesSnapshot.size);

                    // Fetch all jobs
                    const jobsSnapshot = await getDocs(collection(db, 'jobs'));

                    // Count jobs from franchise companies
                    const companyIds = companiesSnapshot.docs.map(doc => doc.id);
                    const franchiseJobs = jobsSnapshot.docs.filter(jobDoc => {
                        const job = jobDoc.data();
                        const companyKey = job.companyId || job.userId;
                        return companyIds.includes(companyKey);
                    });
                    setTotalJobs(franchiseJobs.length);

                    // Fetch subscriptions from franchise users
                    const subscriptionsSnapshot = await getDocs(collection(db, 'subscriptions'));
                    const franchiseUserIds = usersSnapshot.docs.map(doc => doc.id);
                    const franchiseSubscriptions = subscriptionsSnapshot.docs.filter(subDoc => {
                        const sub = subDoc.data();
                        return franchiseUserIds.includes(sub.userId);
                    });
                    setTotalSubscriptions(franchiseSubscriptions.length);

                    // Process recent jobs with company details
                    const companiesData = companiesSnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));

                    const companiesMap = {};
                    companiesData.forEach(company => {
                        companiesMap[company.id] = company;
                    });

                    // Get recent franchise jobs with company details
                    const jobsWithDetails = franchiseJobs.map(jobDoc => {
                        const job = jobDoc.data();
                        const companyKey = job.companyId || job.userId;
                        return {
                            id: jobDoc.id,
                            ...job,
                            companyDetails: companiesMap[companyKey]
                        };
                    }).sort((a, b) => {
                        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                        return dateB - dateA;
                    }).slice(0, 5);

                    setRecentJobs(jobsWithDetails);

                    // Calculate jobs per company for top companies
                    const companyJobCounts = {};
                    franchiseJobs.forEach(jobDoc => {
                        const job = jobDoc.data();
                        const companyKey = job.companyId || job.userId;
                        if (companyKey) {
                            companyJobCounts[companyKey] = (companyJobCounts[companyKey] || 0) + 1;
                        }
                    });

                    const topCompaniesList = companiesData
                        .map(company => ({
                            ...company,
                            jobCount: companyJobCounts[company.id] || 0
                        }))
                        .sort((a, b) => b.jobCount - a.jobCount)
                        .slice(0, 5);

                    setTopCompanies(topCompaniesList);

                    // Process sales performance data (users added per month)
                    const usersData = usersSnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));

                    // Get last 6 months data
                    const last6Months = Array.from({ length: 6 }, (_, i) => {
                        const d = new Date();
                        d.setMonth(d.getMonth() - i);
                        return d;
                    }).reverse();

                    const monthlyData = last6Months.map(date => {
                        const month = date.getMonth();
                        const year = date.getFullYear();
                        const label = date.toLocaleString('default', { month: 'short' });

                        const count = usersData.filter(u => {
                            if (u.createdAt) {
                                const userDate = u.createdAt.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
                                return userDate.getMonth() === month && userDate.getFullYear() === year;
                            }
                            return false;
                        }).length;

                        return { label, count };
                    });

                    setSalesData({
                        labels: monthlyData.map(d => d.label),
                        datasets: [
                            {
                                label: 'Users Added',
                                data: monthlyData.map(d => d.count),
                                borderColor: 'rgb(14, 165, 233)', // sky-500
                                backgroundColor: 'rgba(14, 165, 233, 0.1)',
                                fill: true,
                                tension: 0.4
                            }
                        ]
                    });
                }
            } catch (err) {
                console.error("Error fetching franchise data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchFranchiseData();
    }, [user]);

    if (loading) return <LoadingSpinner />;

    if (!franchiseData) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <h2 className="text-xl font-bold text-gray-800">Franchise Account Not Found</h2>
                    <p className="text-gray-600">Please contact support.</p>
                </div>
            </div>
        );
    }

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top',
            },
            title: {
                display: false
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1
                }
            }
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(referralLink);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
    };

    return (
        <ContentWithAds>
            <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6 max-w-7xl mx-auto pb-24 lg:pb-6">
                {/* Header - Compact Mobile */}
                <div className="mb-3 sm:mb-4 lg:mb-8">
                    <div className="mb-3 sm:mb-4">
                        <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">My Franchise Dashboard</h1>
                        <p className="text-xs sm:text-sm lg:text-base text-gray-600">Welcome back, {franchiseData.ownerName}</p>
                    </div>
                    {/* Desktop Action Buttons - Hidden on Mobile */}
                    <div className="hidden lg:flex gap-3">
                        <button
                            onClick={() => navigate('/franchise/add-employer')}
                            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-medium shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 flex items-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Employer
                        </button>
                        <button
                            onClick={() => navigate('/franchise/companies')}
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-lg font-medium shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 flex items-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            View Companies
                        </button>
                        <button
                            onClick={() => navigate('/franchise/users')}
                            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-medium shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 flex items-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            View All Users
                        </button>
                        <button
                            onClick={() => navigate('/packages')}
                            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 rounded-lg font-medium shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 flex items-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                            Upgrade Plan
                        </button>
                    </div>
                </div>

                {/* Stats Grid - Compact Mobile */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-6 mb-3 sm:mb-4 lg:mb-8">
                    {/* Total Companies Card */}
                    <div
                        className="bg-white rounded-lg sm:rounded-xl shadow-md sm:shadow-lg p-2.5 sm:p-3 lg:p-6 cursor-pointer hover:shadow-xl transition-all duration-300 border-l-2 sm:border-l-4 border-purple-500"
                        onClick={() => navigate('/franchise/companies')}
                    >
                        <div className="flex justify-between items-start mb-1 sm:mb-2 lg:mb-4">
                            <div>
                                <p className="text-gray-500 text-[9px] sm:text-[10px] lg:text-sm font-medium">Companies</p>
                                <h3 className="text-lg sm:text-xl lg:text-3xl font-bold mt-0.5 sm:mt-1 text-gray-800">
                                    {totalCompanies}
                                </h3>
                            </div>
                            <div className="p-1.5 sm:p-2 lg:p-3 bg-purple-100 rounded-md sm:rounded-lg">
                                <svg className="w-3 h-3 sm:w-4 sm:h-4 lg:w-6 lg:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-gray-400 text-[8px] sm:text-[9px] lg:text-xs hidden sm:block">
                            Employers in franchise
                        </p>
                    </div>

                    {/* Total Jobs Card */}
                    <div
                        className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-l-4 border-green-500"
                        onClick={() => navigate('/franchise/jobs')}
                    >
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Active Jobs</p>
                                <h3 className="text-3xl font-bold mt-1 text-gray-800">
                                    {totalJobs}
                                </h3>
                            </div>
                            <div className="p-3 bg-green-100 rounded-lg">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-gray-400 text-xs">
                            Posted by your companies
                        </p>
                    </div>

                    {/* Total Subscriptions Card */}
                    <div
                        className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-l-4 border-blue-500"
                        onClick={() => navigate('/franchise/users')}
                    >
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Subscriptions</p>
                                <h3 className="text-3xl font-bold mt-1 text-gray-800">
                                    {totalSubscriptions}
                                </h3>
                            </div>
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-gray-400 text-xs">
                            Active package subscriptions
                        </p>
                    </div>

                    {/* Total Users Card */}
                    <div
                        className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-l-4 border-cyan-500"
                        onClick={() => navigate('/franchise/users')}
                    >
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Total Users</p>
                                <h3 className="text-3xl font-bold mt-1 text-gray-800">
                                    {totalUsers}
                                </h3>
                            </div>
                            <div className="p-3 bg-cyan-100 rounded-lg">
                                <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-gray-400 text-xs">
                            Registered under your franchise
                        </p>
                    </div>

                    {/* Remaining Quota Card */}
                    <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-emerald-500">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Remaining Quota</p>
                                <h3 className="text-3xl font-bold mt-1 text-gray-800">
                                    {remainingQuota}
                                </h3>
                            </div>
                            <div className="p-3 bg-emerald-100 rounded-lg">
                                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-gray-400 text-xs">
                            of {franchiseData.totalQuota} total quota
                        </p>
                    </div>

                    {/* Remaining Days Card */}
                    <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-orange-500">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Validity Remaining</p>
                                <h3 className="text-3xl font-bold mt-1 text-gray-800">
                                    {remainingDays}
                                </h3>
                            </div>
                            <div className="p-3 bg-orange-100 rounded-lg">
                                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-gray-400 text-xs">
                            Days until {franchiseData.validityEnd}
                        </p>
                    </div>

                    {/* Status Card */}
                    <div className={`bg-white rounded-xl shadow-lg p-6 border-l-4 ${franchiseData.status === 'active'
                        ? 'border-teal-500'
                        : 'border-red-500'
                        }`}>
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Status</p>
                                <h3 className={`text-3xl font-bold mt-1 capitalize ${franchiseData.status === 'active' ? 'text-teal-600' : 'text-red-600'}`}>
                                    {franchiseData.status || 'Active'}
                                </h3>
                            </div>
                            <div className={`p-3 rounded-lg ${franchiseData.status === 'active' ? 'bg-teal-100' : 'bg-red-100'}`}>
                                <svg className={`w-6 h-6 ${franchiseData.status === 'active' ? 'text-teal-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-gray-400 text-xs">
                            Franchise account status
                        </p>
                    </div>
                </div>

                {/* Registration Link Section */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Registration Link</h2>
                                <p className="text-gray-500 text-sm">Share this link to register users under your franchise</p>
                            </div>
                        </div>


                    </div>

                    <div className="flex gap-3">
                        <input
                            type="text"
                            value={referralLink}
                            readOnly
                            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-700 font-mono text-sm"
                        />
                        <button
                            onClick={copyToClipboard}
                            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors flex items-center gap-2"
                        >
                            {copySuccess ? (
                                <>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Copied!
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    Copy Link
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Recent Jobs and Top Companies Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Recent Jobs */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">Recent Jobs</h3>
                            </div>
                            <button
                                onClick={() => navigate('/franchise/companies')}
                                className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                            >
                                View All →
                            </button>
                        </div>

                        {recentJobs.length > 0 ? (
                            <div className="space-y-3">
                                {recentJobs.map((job) => (
                                    <div key={job.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0 mr-2">
                                                <h4 className="font-semibold text-gray-900 text-sm mb-0.5 truncate">{job.title}</h4>
                                                <p className="text-xs text-gray-600 mb-1 truncate">
                                                    {job.companyDetails?.companyName || job.companyDetails?.name || job.company || 'Unknown'}
                                                </p>
                                                <div className="flex items-center gap-2 text-[10px] text-gray-500">
                                                    <span className="truncate max-w-[100px]">📍 {typeof job.location === 'object'
                                                        ? `${job.location.city || ''}`
                                                        : job.location || 'N/A'}</span>
                                                    <span>👥 {job.applicationCount || 0}</span>
                                                </div>
                                            </div>
                                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${job.status === 'active'
                                                ? 'bg-green-100 text-green-700'
                                                : job.status === 'closed'
                                                    ? 'bg-red-100 text-red-700'
                                                    : 'bg-yellow-100 text-yellow-700'
                                                }`}>
                                                {job.status?.toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6">
                                <div className="text-gray-400 text-2xl mb-2">💼</div>
                                <p className="text-gray-500 text-sm">No recent jobs</p>
                            </div>
                        )}
                    </div>

                    {/* Top Companies */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">Top Companies</h3>
                            </div>
                            <button
                                onClick={() => navigate('/franchise/companies')}
                                className="text-purple-600 hover:text-purple-700 text-xs font-medium"
                            >
                                View All →
                            </button>
                        </div>

                        {topCompanies.length > 0 ? (
                            <div className="space-y-3">
                                {topCompanies.map((company, index) => (
                                    <div key={company.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                        <div className="w-6 h-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                            #{index + 1}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-semibold text-gray-900 text-sm truncate">{company.companyName || company.name}</h4>
                                            <p className="text-[10px] text-gray-600 truncate">{company.industry || 'N/A'}</p>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <div className="text-sm font-bold text-purple-600">{company.jobCount}</div>
                                            <div className="text-[10px] text-gray-500">jobs</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6">
                                <div className="text-gray-400 text-2xl mb-2">🏢</div>
                                <p className="text-gray-500 text-sm">No companies yet</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sales Performance Chart */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
                    <div className="px-6 py-4 border-b border-gray-100">
                        <h3 className="text-lg font-bold text-gray-800">Sales Performance</h3>
                        <p className="text-sm text-gray-500">Users added over the last 6 months</p>
                    </div>
                    <div className="p-6">
                        <div style={{ height: '300px' }}>
                            <Line data={salesData} options={chartOptions} />
                        </div>
                    </div>
                </div>

                {/* Details Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100">
                        <h3 className="text-lg font-bold text-gray-800">Franchise Details</h3>
                    </div>
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-500">Franchise Name</label>
                            <p className="text-gray-900 font-medium mt-1">{franchiseData.name}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-500">Owner Name</label>
                            <p className="text-gray-900 font-medium mt-1">{franchiseData.ownerName}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-500">Email</label>
                            <p className="text-gray-900 font-medium mt-1">{franchiseData.email}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-500">Phone</label>
                            <p className="text-gray-900 font-medium mt-1">{franchiseData.phone}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-500">Location</label>
                            <p className="text-gray-900 font-medium mt-1">{franchiseData.city}, {franchiseData.state}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-500">Franchise ID</label>
                            <p className="text-gray-900 font-medium mt-1 font-mono text-sm">{franchiseId}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Navigation Bar - Mobile Only */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
                <div className="grid grid-cols-4 gap-1">
                    <button
                        onClick={() => navigate('/franchise/add-employer')}
                        className="flex flex-col items-center justify-center py-2.5 px-2 text-orange-600 hover:bg-orange-50 transition-colors"
                    >
                        <svg className="w-5 h-5 mb-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span className="text-[9px] font-medium">Add</span>
                    </button>
                    <button
                        onClick={() => navigate('/franchise/companies')}
                        className="flex flex-col items-center justify-center py-2.5 px-2 text-blue-600 hover:bg-blue-50 transition-colors"
                    >
                        <svg className="w-5 h-5 mb-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="text-[9px] font-medium">Companies</span>
                    </button>
                    <button
                        onClick={() => navigate('/franchise/users')}
                        className="flex flex-col items-center justify-center py-2.5 px-2 text-cyan-600 hover:bg-cyan-50 transition-colors"
                    >
                        <svg className="w-5 h-5 mb-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="text-[9px] font-medium">Users</span>
                    </button>
                    <button
                        onClick={() => navigate('/packages')}
                        className="flex flex-col items-center justify-center py-2.5 px-2 text-purple-600 hover:bg-purple-50 transition-colors"
                    >
                        <svg className="w-5 h-5 mb-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        <span className="text-[9px] font-medium">Upgrade</span>
                    </button>
                </div>
            </div>
        </ContentWithAds>
    );
};

export default FranchiseDashboard;
